import React, { FC, Fragment, useState, useEffect } from 'react';
import { Box, Chip, Link, TableCell, TableRow, Typography } from '@mui/material';
import { User } from 'models/user';
import { useTranslation } from 'react-i18next';
import ActiveIndicator from 'components/users/shared/active-indicator/active-indicator.component';
import { useMutation } from '@apollo/client';
import { whiteLabelDentistAccessTemplate } from 'apollo/templates/user';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { getLabDefaultClinicName } from 'utils/lab-dentist-default-clinic';

type ClientDirectoryDentistItemProps = {
    isWhiteLabel?: boolean;
    showDefaultClinicColumn?: boolean;
    mainClinicId?: string;
    dentist: User;
    onUpdateDentist: (dentist: User) => void;
    onUserUpdated: (user: User) => void;
};

const ClientDirectoryDentistItem: FC<ClientDirectoryDentistItemProps> = ({
    isWhiteLabel,
    showDefaultClinicColumn,
    mainClinicId,
    dentist,
    onUpdateDentist,
    onUserUpdated,
}: ClientDirectoryDentistItemProps) => {
    const { t: translate } = useTranslation(['common', 'client-directory']);
    const { showBoundary } = useDenthubErrorBoundary();
    const translatedRole = translate('common:' + dentist.role.toUpperCase());
    const [isAccessGranted, setIsAccessGranted] = useState(false);
    const lab = currentOrgVar();
    const localDentistId = dentist.dentistLocalIds?.find((entry) => entry.lab === lab._id?.toString())?.localId;
    const defaultClinicName = getLabDefaultClinicName(dentist, mainClinicId);

    const [whiteLabelDentistAccess] = useMutation(whiteLabelDentistAccessTemplate, {
        onCompleted: ({ whiteLabelDentistAccess }) => {
            onUserUpdated(whiteLabelDentistAccess);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        if (dentist) {
            const hasWhiteLabelDentistAccess = dentist?.whiteLabelAccess?.some(
                (elem) => elem?.toString() === lab._id?.toString(),
            );
            setIsAccessGranted(hasWhiteLabelDentistAccess || false);
        }
    }, [dentist]);

    const handleWhiteLabelDentistAccess = async (dentistId: string) => {
        await whiteLabelDentistAccess({ variables: { dentistId: dentistId } });
    };

    return (
        <Fragment>
            <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => onUpdateDentist(dentist)}>
                <Fragment>
                    <TableCell sx={{ width: 10 }} align="center">
                        <ActiveIndicator isActive={!!dentist?.active} />
                    </TableCell>
                    {isWhiteLabel ? (
                        !dentist.local ? (
                            <TableCell>
                                <Typography variant="subtitle2" color={isAccessGranted ? 'green' : 'gray'}>
                                    {isAccessGranted ? translate('common:ACTIVE') : translate('common:INACTIVE')}
                                </Typography>
                                <Box>
                                    {!dentist.local && (
                                        <Fragment>
                                            {isAccessGranted ? (
                                                <Link
                                                    component="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setIsAccessGranted(false);
                                                        handleWhiteLabelDentistAccess(dentist._id);
                                                    }}
                                                >
                                                    {translate('common:INACTIVATE')}
                                                </Link>
                                            ) : (
                                                <Link
                                                    component="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setIsAccessGranted(true);
                                                        handleWhiteLabelDentistAccess(dentist._id);
                                                    }}
                                                >
                                                    {translate('common:ACTIVATE')}
                                                </Link>
                                            )}
                                        </Fragment>
                                    )}
                                </Box>
                            </TableCell>
                        ) : (
                            <TableCell>-</TableCell>
                        )
                    ) : undefined}
                    <TableCell sx={{ width: 300 }}>
                        <span>
                            {dentist.name.firstName} {dentist.name.lastName}
                        </span>
                        {localDentistId && (
                            <Typography variant="body2" color="text.secondary">
                                {localDentistId}
                            </Typography>
                        )}
                    </TableCell>
                    <TableCell>{dentist.email || '-'}</TableCell>
                    {showDefaultClinicColumn ? <TableCell>{defaultClinicName || '-'}</TableCell> : undefined}
                    <TableCell>{translatedRole}</TableCell>
                    <TableCell align="right">
                        <Chip
                            label={
                                dentist.local
                                    ? translate('client-directory:MY_DENTIST')
                                    : translate('client-directory:DENTHUB_CONNECTED_DENTIST')
                            }
                            size="medium"
                            color={dentist.local ? 'info' : 'secondary'}
                        />
                    </TableCell>
                </Fragment>
            </TableRow>
        </Fragment>
    );
};

export default ClientDirectoryDentistItem;
